import {
  createAuthenticatedApi,
  createOpenApiConfiguration,
  getApiErrorMessage,
} from "@/lib/utils/api";
import { GlobalApi } from "@/openapi/generated";
import { ResponseError } from "@/openapi/generated/runtime";
import {
  DEFAULT_STUDIO_FEATURE_SETTINGS,
  DEFAULT_STUDIO_FEATURE_SETTINGS_PERSISTED,
  FEATURES_APPOINTMENTS_ENABLED_KEY,
  FEATURES_CHOREOGRAPHER_PORTAL_ENABLED_KEY,
  FEATURES_CONTRACTORS_ENABLED_KEY,
  FEATURES_HOMEPAGE_ENABLED_KEY,
  FEATURES_MAKEUPS_ENABLED_KEY,
  FEATURES_SETTINGS_GROUP,
  FEATURES_TICKETS_ENABLED_KEY,
  FEATURES_TIME_CLOCK_ENABLED_KEY,
  formatStudioFeatureFlag,
  parseStudioFeatureFlag,
  type StudioFeatureSettings,
  type StudioFeatureSettingsPersisted,
} from "./feature-settings-defaults";

export type StudioFeatureSettingsResult = StudioFeatureSettings & {
  persisted: StudioFeatureSettingsPersisted;
};

function createGlobalApi(accessToken?: string): GlobalApi {
  if (accessToken) {
    return createAuthenticatedApi(GlobalApi, accessToken);
  }
  return new GlobalApi(createOpenApiConfiguration());
}

function isNotFoundOrBadRequest(error: unknown): boolean {
  return (
    error instanceof ResponseError &&
    (error.response.status === 404 || error.response.status === 400)
  );
}

function settingsMapFromRows(
  rows: { key: string; value: string }[],
): Map<string, string> {
  return new Map(rows.map((row) => [row.key, row.value]));
}

export async function fetchStudioFeatureSettings(
  tenantId: number,
  options?: { accessToken?: string },
): Promise<StudioFeatureSettingsResult> {
  const api = createGlobalApi(options?.accessToken);

  try {
    const rows = await api.apiGlobalSettingsByGroupGet({
      group: FEATURES_SETTINGS_GROUP,
      xTenantID: tenantId,
    });
    const map = settingsMapFromRows(rows);
    const makeupsRaw = map.get(FEATURES_MAKEUPS_ENABLED_KEY);
    const appointmentsRaw = map.get(FEATURES_APPOINTMENTS_ENABLED_KEY);
    const timeClockRaw = map.get(FEATURES_TIME_CLOCK_ENABLED_KEY);
    const homepageRaw = map.get(FEATURES_HOMEPAGE_ENABLED_KEY);
    const choreographerRaw = map.get(FEATURES_CHOREOGRAPHER_PORTAL_ENABLED_KEY);
    const contractorsRaw = map.get(FEATURES_CONTRACTORS_ENABLED_KEY);
    const ticketsRaw = map.get(FEATURES_TICKETS_ENABLED_KEY);

    return {
      makeupsEnabled: parseStudioFeatureFlag(
        makeupsRaw,
        DEFAULT_STUDIO_FEATURE_SETTINGS.makeupsEnabled,
      ),
      appointmentsEnabled: parseStudioFeatureFlag(
        appointmentsRaw,
        DEFAULT_STUDIO_FEATURE_SETTINGS.appointmentsEnabled,
      ),
      timeClockEnabled: parseStudioFeatureFlag(
        timeClockRaw,
        DEFAULT_STUDIO_FEATURE_SETTINGS.timeClockEnabled,
      ),
      homepageEnabled: parseStudioFeatureFlag(
        homepageRaw,
        DEFAULT_STUDIO_FEATURE_SETTINGS.homepageEnabled,
      ),
      choreographerPortalEnabled: parseStudioFeatureFlag(
        choreographerRaw,
        DEFAULT_STUDIO_FEATURE_SETTINGS.choreographerPortalEnabled,
      ),
      contractorsEnabled: parseStudioFeatureFlag(
        contractorsRaw,
        DEFAULT_STUDIO_FEATURE_SETTINGS.contractorsEnabled,
      ),
      ticketsEnabled: parseStudioFeatureFlag(
        ticketsRaw,
        DEFAULT_STUDIO_FEATURE_SETTINGS.ticketsEnabled,
      ),
      persisted: {
        makeups: makeupsRaw != null,
        appointments: appointmentsRaw != null,
        timeClock: timeClockRaw != null,
        homepage: homepageRaw != null,
        choreographerPortal: choreographerRaw != null,
        contractors: contractorsRaw != null,
        tickets: ticketsRaw != null,
      },
    };
  } catch (error) {
    if (!isNotFoundOrBadRequest(error)) {
      // Fall through to defaults when group is missing.
    }
    return {
      ...DEFAULT_STUDIO_FEATURE_SETTINGS,
      persisted: { ...DEFAULT_STUDIO_FEATURE_SETTINGS_PERSISTED },
    };
  }
}

async function upsertFeatureSetting(
  tenantId: number,
  key: string,
  value: string,
  accessToken: string,
  persisted: boolean,
  errorLabel: string,
): Promise<void> {
  const api = createAuthenticatedApi(GlobalApi, accessToken);

  const createRequest = {
    xTenantID: tenantId,
    createSettingRequest: {
      key,
      value,
      group: FEATURES_SETTINGS_GROUP,
    },
  };
  const updateRequest = {
    xTenantID: tenantId,
    updateSettingRequest: {
      key,
      value,
      group: FEATURES_SETTINGS_GROUP,
    },
  };

  const throwUpsertError = async (
    error: unknown,
    action: "create" | "update",
  ) => {
    const message = await getApiErrorMessage(
      error,
      `Failed to ${action} ${errorLabel}`,
    );
    throw new Error(message);
  };

  if (persisted) {
    try {
      await api.apiGlobalSettingsPut(updateRequest);
      return;
    } catch (error) {
      if (!isNotFoundOrBadRequest(error)) {
        await throwUpsertError(error, "update");
      }
    }
  }

  try {
    await api.apiGlobalSettingsPost(createRequest);
    return;
  } catch (error) {
    if (!(error instanceof ResponseError && error.response.status === 409)) {
      await throwUpsertError(error, "create");
    }
  }

  try {
    await api.apiGlobalSettingsPut(updateRequest);
  } catch (error) {
    await throwUpsertError(error, "update");
  }
}

export async function upsertStudioFeatureSettings(
  tenantId: number,
  request: StudioFeatureSettings & {
    persisted: StudioFeatureSettingsPersisted;
  },
  accessToken: string,
): Promise<StudioFeatureSettingsResult> {
  await Promise.all([
    upsertFeatureSetting(
      tenantId,
      FEATURES_MAKEUPS_ENABLED_KEY,
      formatStudioFeatureFlag(request.makeupsEnabled),
      accessToken,
      request.persisted.makeups,
      "makeups feature setting",
    ),
    upsertFeatureSetting(
      tenantId,
      FEATURES_APPOINTMENTS_ENABLED_KEY,
      formatStudioFeatureFlag(request.appointmentsEnabled),
      accessToken,
      request.persisted.appointments,
      "appointments feature setting",
    ),
    upsertFeatureSetting(
      tenantId,
      FEATURES_TIME_CLOCK_ENABLED_KEY,
      formatStudioFeatureFlag(request.timeClockEnabled),
      accessToken,
      request.persisted.timeClock,
      "time clock feature setting",
    ),
    upsertFeatureSetting(
      tenantId,
      FEATURES_HOMEPAGE_ENABLED_KEY,
      formatStudioFeatureFlag(request.homepageEnabled),
      accessToken,
      request.persisted.homepage,
      "homepage feature setting",
    ),
    upsertFeatureSetting(
      tenantId,
      FEATURES_CHOREOGRAPHER_PORTAL_ENABLED_KEY,
      formatStudioFeatureFlag(request.choreographerPortalEnabled),
      accessToken,
      request.persisted.choreographerPortal,
      "choreographer portal feature setting",
    ),
    upsertFeatureSetting(
      tenantId,
      FEATURES_CONTRACTORS_ENABLED_KEY,
      formatStudioFeatureFlag(request.contractorsEnabled),
      accessToken,
      request.persisted.contractors,
      "contractors feature setting",
    ),
    upsertFeatureSetting(
      tenantId,
      FEATURES_TICKETS_ENABLED_KEY,
      formatStudioFeatureFlag(request.ticketsEnabled),
      accessToken,
      request.persisted.tickets,
      "tickets feature setting",
    ),
  ]);

  return {
    makeupsEnabled: request.makeupsEnabled,
    appointmentsEnabled: request.appointmentsEnabled,
    timeClockEnabled: request.timeClockEnabled,
    homepageEnabled: request.homepageEnabled,
    choreographerPortalEnabled: request.choreographerPortalEnabled,
    contractorsEnabled: request.contractorsEnabled,
    ticketsEnabled: request.ticketsEnabled,
    persisted: {
      makeups: true,
      appointments: true,
      timeClock: true,
      homepage: true,
      choreographerPortal: true,
      contractors: true,
      tickets: true,
    },
  };
}
